import type { AuditOutcome, CurrentUser } from '@occ/contracts';
import { SYSTEM_AUDIT_ACTIONS, actionNames } from '@occ/contracts';
import { useCan, useGetIdentity } from '@refinedev/core';
import type { ColumnDef } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ErrorNotice } from '../../components/ErrorNotice';
import {
  Alert,
  Badge,
  Button,
  Card,
  DataTable,
  DataTableColumnHeader,
  EmptyState,
  Loading,
  Modal,
  Select,
} from '../../components/ui';
import { downloadAuditCsv, fetchAudit, type AuditFilters, type AuditRow } from '../../data/audit';
import { listUsers, type UserRecord } from '../../data/admin';
import { useHierarchy } from '../../hierarchy/HierarchyProvider';

/**
 * Bitácora (Módulo 10, RF-09).
 *
 * Cubre panel y MCP por igual porque ambos entran por la misma puerta: la columna
 * «canal» es lo que hace visible que una acción vino de un agente de lenguaje
 * natural y no de un humano pulsando un botón. Sin ella, la paridad de MCP sería
 * un agujero de trazabilidad en vez de una función.
 *
 * La exportación se genera en el navegador con **los filtros que están aplicados**,
 * no pidiendo un CSV al backend: así lo que se descarga es exactamente lo que se
 * está viendo, y no hay un segundo camino que pueda discrepar de la tabla.
 *
 * **El backend no expone `GET /audit` todavía.** `AuditService` escribe cada
 * acción, pero no hay controlador que consulte. La pantalla está entera y dice qué
 * falta.
 */

const OUTCOMES: Array<{ value: AuditOutcome | ''; label: string }> = [
  { value: '', label: 'Todos' },
  { value: 'success', label: 'Correctos' },
  { value: 'denied', label: 'Denegados' },
  { value: 'error', label: 'Con error' },
];

const OUTCOME_TONE: Record<AuditOutcome, 'ok' | 'warn' | 'neutral'> = {
  success: 'ok',
  denied: 'warn',
  error: 'warn',
};

const OUTCOME_LABEL: Record<AuditOutcome, string> = {
  success: 'correcto',
  denied: 'denegado',
  error: 'error',
};

/**
 * Eventos que el backend audita pero que **no** están declarados en
 * `SYSTEM_AUDIT_ACTIONS`.
 *
 * Se sacaron leyendo las llamadas a `AuditService` del backend. Es una laguna del
 * contrato, no de esta pantalla: si no estuvieran aquí, la bitácora tendría filas
 * que no se pueden filtrar por su propia acción. Cuando suban al contrato, esta
 * lista se queda vacía y el `Set` la absorbe sin duplicar nada.
 */
const UNDECLARED_AUDIT_ACTIONS = ['enrollment.ca_rotated', 'terminal.redeem', 'session.closed'];

/**
 * Acciones filtrables: las del catálogo más los eventos propios del OCC.
 *
 * Se importan de `@occ/contracts` en vez de escribirse: una acción nueva —o un
 * evento de sistema nuevo, como los de alertas y grabaciones que acaban de
 * entrar— se puede filtrar el mismo día, sin tocar esta pantalla.
 */
const FILTERABLE_ACTIONS = [
  ...new Set<string>([...actionNames, ...SYSTEM_AUDIT_ACTIONS, ...UNDECLARED_AUDIT_ACTIONS]),
].sort((left, right) => left.localeCompare(right));

const today = (): string => new Date().toISOString().slice(0, 10);
const daysAgo = (days: number): string =>
  new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10);

export function AuditPage() {
  const { data: canRead } = useCan({ resource: 'audit', action: 'list' });
  const { data: me } = useGetIdentity<CurrentUser>();
  const { tree, nodes } = useHierarchy();

  const [filters, setFilters] = useState<AuditFilters>(() => ({
    from: daysAgo(7),
    to: today(),
    outcome: '',
  }));
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [inspecting, setInspecting] = useState<AuditRow | null>(null);

  /** Usuarios para el desplegable. Si `/users` tampoco existe, se filtra por nodo. */
  const [users, setUsers] = useState<UserRecord[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await fetchAudit(filters));
      setError(null);
    } catch (cause) {
      // No se vacía la tabla: si el técnico ya tenía filas cargadas y un filtro
      // nuevo falla, perderlas sería peor que dejarlas con el aviso encima.
      setError(cause);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    listUsers()
      .then(setUsers)
      .catch(() => setUsers([]));
  }, []);

  const columns = useMemo<ColumnDef<AuditRow>[]>(
    () => [
      {
        id: 'cuando',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Cuándo" />,
        accessorFn: (row) => row.occurredAt?.getTime() ?? 0,
        meta: { label: 'Cuándo' },
        cell: ({ row }) => (
          <span className="whitespace-nowrap">
            {row.original.occurredAt?.toLocaleString('es') ?? '—'}
          </span>
        ),
      },
      {
        id: 'usuario',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Usuario" />,
        accessorFn: (row) => row.actorEmail ?? row.actorUserId?.slice(0, 8) ?? 'sistema',
        meta: { label: 'Usuario' },
        cell: ({ getValue }) => <span className="opacity-80">{getValue<string>()}</span>,
      },
      {
        id: 'canal',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Canal" />,
        accessorFn: (row) => row.channel,
        meta: { label: 'Canal' },
        cell: ({ row }) => (
          <Badge tone={row.original.channel === 'mcp' ? 'warn' : 'neutral'}>
            {row.original.channel}
          </Badge>
        ),
      },
      {
        id: 'accion',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Acción" />,
        accessorFn: (row) => row.action,
        meta: { label: 'Acción' },
        cell: ({ getValue }) => <span className="font-mono text-xs">{getValue<string>()}</span>,
      },
      {
        id: 'nodo',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Nodo" />,
        accessorFn: (row) =>
          row.nodeAlias ??
          nodes.find((node) => node.id === row.nodeId)?.alias ??
          (row.nodeId ? `${row.nodeId.slice(0, 8)}…` : '—'),
        meta: { label: 'Nodo' },
        cell: ({ getValue }) => <span className="opacity-70">{getValue<string>()}</span>,
      },
      {
        id: 'resultado',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Resultado" />,
        accessorFn: (row) => row.outcome,
        meta: { label: 'Resultado' },
        cell: ({ row }) => (
          <Badge tone={OUTCOME_TONE[row.original.outcome]}>
            {OUTCOME_LABEL[row.original.outcome]}
          </Badge>
        ),
      },
      {
        id: 'duracion',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Duración" />,
        accessorFn: (row) => row.durationMs ?? -1,
        meta: { label: 'Duración' },
        cell: ({ row }) => (
          <span className="text-right opacity-70">
            {row.original.durationMs === null ? '—' : `${row.original.durationMs} ms`}
          </span>
        ),
      },
    ],
    [nodes],
  );

  return (
    <section className="grid gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Bitácora</h1>
          <p className="text-sm opacity-60">
            Registro append-only de todo lo que pasó por la puerta, venga del panel o de MCP.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" disabled={loading} onClick={() => void load()}>
            Recargar
          </Button>
          <Button
            variant="secondary"
            disabled={rows.length === 0}
            title={
              rows.length === 0
                ? 'Nada que exportar'
                : `Exporta las ${rows.length} filas que se están viendo`
            }
            onClick={() => downloadAuditCsv(rows)}
          >
            Exportar CSV ({rows.length})
          </Button>
        </div>
      </div>

      {!canRead?.can && <Alert>{canRead?.reason ?? 'La bitácora está fuera de tu alcance.'}</Alert>}

      <Card title="Filtros">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Select
            label="Usuario"
            value={filters.actorUserId ?? ''}
            onChange={(event) => setFilters({ ...filters, actorUserId: event.target.value })}
          >
            <option value="">Todos</option>
            {me && <option value={me.id}>Yo ({me.email})</option>}
            {users
              .filter((user) => user.id !== me?.id)
              .map((user) => (
                <option key={user.id} value={user.id}>
                  {user.email}
                </option>
              ))}
          </Select>

          {/*
            El cliente **acota la lista de nodos**, no la consulta: el backend
            filtra por nodo y no por cliente. Antes se mandaba `clientId` en la
            petición y el backend lo descartaba en silencio, así que el resultado
            parecía filtrado y no lo estaba — un filtro que miente es peor que uno
            que no existe. Al cambiar de cliente se limpia el nodo elegido si ya no
            pertenece, porque un nodo que no está en la lista tampoco se puede
            quitar.
          */}
          <Select
            label="Cliente"
            value={filters.clientId ?? ''}
            onChange={(event) => {
              const clientId = event.target.value;
              const sigueValiendo =
                !clientId ||
                nodes.some((node) => node.id === filters.nodeId && node.clientId === clientId);
              setFilters({
                ...filters,
                clientId,
                nodeId: sigueValiendo ? filters.nodeId : undefined,
              });
            }}
          >
            <option value="">Todos</option>
            {(tree?.clients ?? []).map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </Select>

          <Select
            label="Nodo"
            value={filters.nodeId ?? ''}
            onChange={(event) => setFilters({ ...filters, nodeId: event.target.value })}
          >
            <option value="">Todos</option>
            {nodes
              .filter((node) => !filters.clientId || node.clientId === filters.clientId)
              .map((node) => (
                <option key={node.id} value={node.id}>
                  {node.clientName} · {node.alias}
                </option>
              ))}
          </Select>

          <Select
            label="Acción"
            value={filters.action ?? ''}
            onChange={(event) => setFilters({ ...filters, action: event.target.value })}
          >
            <option value="">Todas</option>
            {FILTERABLE_ACTIONS.map((action) => (
              <option key={action} value={action}>
                {action}
              </option>
            ))}
          </Select>

          <Select
            label="Resultado"
            value={filters.outcome ?? ''}
            onChange={(event) =>
              setFilters({ ...filters, outcome: event.target.value as AuditOutcome | '' })
            }
          >
            {OUTCOMES.map((outcome) => (
              <option key={outcome.value} value={outcome.value}>
                {outcome.label}
              </option>
            ))}
          </Select>

          <div className="grid grid-cols-2 gap-2">
            <label className="grid gap-1 text-sm">
              <span className="opacity-70">Desde</span>
              <input
                type="date"
                value={filters.from ?? ''}
                onChange={(event) => setFilters({ ...filters, from: event.target.value })}
                className="rounded border border-black/15 dark:border-white/20 bg-transparent px-2.5 py-1.5 outline-none focus:border-[var(--occ-brand)]"
              />
            </label>
            <label className="grid gap-1 text-sm">
              <span className="opacity-70">Hasta</span>
              <input
                type="date"
                value={filters.to ?? ''}
                onChange={(event) => setFilters({ ...filters, to: event.target.value })}
                className="rounded border border-black/15 dark:border-white/20 bg-transparent px-2.5 py-1.5 outline-none focus:border-[var(--occ-brand)]"
              />
            </label>
          </div>
        </div>
      </Card>

      {error !== null && (
        <ErrorNotice
          error={error}
          what="Consultar la bitácora"
          pendingHint="Falta el controlador de `/audit`: la tabla `audit_logs` ya se escribe en cada acción."
        />
      )}

      <Card>
        {loading && rows.length === 0 ? (
          <Loading label="Consultando la bitácora…" />
        ) : rows.length === 0 ? (
          <EmptyState title={error !== null ? 'Sin datos' : 'Ninguna entrada con estos filtros'}>
            {error === null && 'Prueba a ampliar el rango de fechas.'}
          </EmptyState>
        ) : (
          <DataTable
            columns={columns}
            data={rows}
            onRowClick={setInspecting}
            emptyMessage="Ninguna entrada con estos filtros."
          />
        )}
      </Card>

      <Modal
        open={inspecting !== null}
        wide
        title="Entrada de la bitácora"
        onClose={() => setInspecting(null)}
      >
        {inspecting && (
          <div className="grid gap-3 text-sm">
            <dl className="grid grid-cols-[9rem_1fr] gap-y-1">
              <dt className="opacity-60">Cuándo</dt>
              <dd>{inspecting.occurredAt?.toISOString() ?? '—'}</dd>
              <dt className="opacity-60">Acción</dt>
              <dd className="font-mono text-xs">{inspecting.action}</dd>
              <dt className="opacity-60">Canal</dt>
              <dd>{inspecting.channel}</dd>
              <dt className="opacity-60">Usuario</dt>
              <dd className="break-all">
                {inspecting.actorEmail ?? inspecting.actorUserId ?? '—'}
              </dd>
              <dt className="opacity-60">Nodo</dt>
              <dd className="break-all">{inspecting.nodeId ?? '—'}</dd>
              <dt className="opacity-60">Sesión</dt>
              <dd className="break-all">{inspecting.sessionId ?? '—'}</dd>
              <dt className="opacity-60">Resultado</dt>
              <dd>{OUTCOME_LABEL[inspecting.outcome]}</dd>
              <dt className="opacity-60">Motivo</dt>
              <dd>{inspecting.reason ?? '—'}</dd>
            </dl>

            <div className="grid gap-1">
              <div className="text-xs uppercase tracking-wide opacity-50">
                Detalle (nunca contiene secretos)
              </div>
              <pre className="max-h-64 overflow-auto rounded bg-black/5 dark:bg-white/5 p-3 text-xs">
                {JSON.stringify(inspecting.detail, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </Modal>
    </section>
  );
}
